import React, { useEffect, useState, useMemo } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Search, Edit, Trash2, CheckCircle, XCircle, FileText, DollarSign, Download, FilePlus, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';

const Orcamentos = () => {
  const [orcamentos, setOrcamentos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [atendimentos, setAtendimentos] = useState([]);
  const [tiposDocumentos, setTiposDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [ordenacao, setOrdenacao] = useState('recente');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(false);
  const [isNovoTipoDialogOpen, setIsNovoTipoDialogOpen] = useState(false);
  const [editingOrcamento, setEditingOrcamento] = useState(null);
  const [selectedOrcamentoForPdf, setSelectedOrcamentoForPdf] = useState(null);
  const [selectedDocumentos, setSelectedDocumentos] = useState([]);
  const [novoTipoDoc, setNovoTipoDoc] = useState({ nome: '', descricao: '' });
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [vinculoTipo, setVinculoTipo] = useState('cliente');
  const [formData, setFormData] = useState({
    atendimento_id: '',
    cliente_id: '',
    descricao: '',
    valor_a_vista: '',
    valor_parcelado: '',
    parcelas_parcelado: '1',
    valor_entrada: '',
    valor_parcela_entrada: '',
    parcelas_entrada: '1',
    validade_dias: 30,
    observacoes: '',
    status: 'Pendente'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [orcamentosRes, clientesRes, atendimentosRes, tiposRes] = await Promise.all([
        api.getOrcamentos(),
        api.getClientes(),
        api.getAtendimentos(),
        api.getTiposDocumentos()
      ]);
      setOrcamentos(orcamentosRes.data);
      setClientes(clientesRes.data);
      setAtendimentos(atendimentosRes.data.filter(a => a.status !== 'Convertido' && a.status !== 'Arquivado'));
      setTiposDocumentos(tiposRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        valor_a_vista: parseFloat(formData.valor_a_vista),
        valor_parcelado: parseFloat(formData.valor_parcelado),
        parcelas_parcelado: parseInt(formData.parcelas_parcelado),
        valor_entrada: formData.valor_entrada ? parseFloat(formData.valor_entrada) : null,
        valor_parcela_entrada: formData.valor_parcela_entrada ? parseFloat(formData.valor_parcela_entrada) : null,
        parcelas_entrada: parseInt(formData.parcelas_entrada),
        validade_dias: parseInt(formData.validade_dias),
        cliente_id: vinculoTipo === 'cliente' ? formData.cliente_id : null,
        atendimento_id: vinculoTipo === 'atendimento' ? formData.atendimento_id : null
      };

      if (editingOrcamento) {
        await api.updateOrcamento(editingOrcamento.id, submitData);
        toast.success('Orçamento atualizado com sucesso!');
      } else {
        await api.createOrcamento(submitData);
        toast.success('Orçamento criado com sucesso!');
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Erro ao salvar orçamento:', error);
      toast.error('Erro ao salvar orçamento');
    }
  };

  const handleEdit = (orcamento) => {
    setEditingOrcamento(orcamento);
    setVinculoTipo(orcamento.cliente_id ? 'cliente' : 'atendimento');
    setFormData({
      atendimento_id: orcamento.atendimento_id || '',
      cliente_id: orcamento.cliente_id || '',
      descricao: orcamento.descricao,
      valor_a_vista: (orcamento.valor_a_vista || orcamento.valor_total || '').toString(),
      valor_parcelado: (orcamento.valor_parcelado || orcamento.valor_total || '').toString(),
      parcelas_parcelado: (orcamento.parcelas_parcelado || 1).toString(),
      valor_entrada: (orcamento.valor_entrada || '').toString(),
      valor_parcela_entrada: (orcamento.valor_parcela_entrada || '').toString(),
      parcelas_entrada: (orcamento.parcelas_entrada || 1).toString(),
      validade_dias: orcamento.validade_dias,
      observacoes: orcamento.observacoes || '',
      status: orcamento.status
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (orcamentoId) => {
    if (!window.confirm('Deseja realmente excluir este orçamento?')) return;
    try {
      await api.deleteOrcamento(orcamentoId);
      toast.success('Orçamento excluído com sucesso!');
      loadData();
    } catch (error) {
      console.error('Erro ao excluir orçamento:', error);
      toast.error('Erro ao excluir orçamento');
    }
  };

  const handleAprovar = async (orcamentoId) => {
    try {
      await api.aprovarOrcamento(orcamentoId);
      toast.success('Orçamento aprovado!');
      loadData();
    } catch (error) {
      console.error('Erro ao aprovar orçamento:', error);
      toast.error('Erro ao aprovar orçamento');
    }
  };

  const handleRejeitar = async (orcamentoId) => {
    try {
      await api.rejeitarOrcamento(orcamentoId);
      toast.success('Orçamento rejeitado');
      loadData();
    } catch (error) {
      console.error('Erro ao rejeitar orçamento:', error);
      toast.error('Erro ao rejeitar orçamento');
    }
  };

  const handleOpenPdfDialog = (orcamento) => {
    setSelectedOrcamentoForPdf(orcamento);
    setSelectedDocumentos([]);
    setIsPdfDialogOpen(true);
  };

  const handleToggleDocumento = (docNome) => {
    setSelectedDocumentos(prev => 
      prev.includes(docNome) 
        ? prev.filter(d => d !== docNome)
        : [...prev, docNome]
    );
  };

  const handleGerarPdf = async () => {
    if (!selectedOrcamentoForPdf) return;
    
    setGeneratingPdf(true);
    try {
      const response = await api.gerarPdfOrcamento(selectedOrcamentoForPdf.id, selectedDocumentos);
      
      // Criar blob e download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `orcamento_${selectedOrcamentoForPdf.numero || 'N_A'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF gerado com sucesso!');
      setIsPdfDialogOpen(false);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleCreateNovoTipo = async (e) => {
    e.preventDefault();
    try {
      await api.createTipoDocumento(novoTipoDoc);
      toast.success('Tipo de documento criado!');
      setNovoTipoDoc({ nome: '', descricao: '' });
      setIsNovoTipoDialogOpen(false);
      // Recarregar tipos
      const tiposRes = await api.getTiposDocumentos();
      setTiposDocumentos(tiposRes.data);
    } catch (error) {
      console.error('Erro ao criar tipo:', error);
      toast.error('Erro ao criar tipo de documento');
    }
  };

  const handleDeleteTipo = async (tipoId) => {
    try {
      await api.deleteTipoDocumento(tipoId);
      toast.success('Tipo de documento removido!');
      const tiposRes = await api.getTiposDocumentos();
      setTiposDocumentos(tiposRes.data);
    } catch (error) {
      console.error('Erro ao excluir tipo:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir tipo');
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingOrcamento(null);
    setVinculoTipo('cliente');
    setFormData({
      atendimento_id: '',
      cliente_id: '',
      descricao: '',
      valor_a_vista: '',
      valor_parcelado: '',
      parcelas_parcelado: '1',
      valor_entrada: '',
      valor_parcela_entrada: '',
      parcelas_entrada: '1',
      validade_dias: 30,
      observacoes: '',
      status: 'Pendente'
    });
  };

  const filteredOrcamentos = useMemo(() => {
    let result = orcamentos.filter(orcamento => {
      const nome = orcamento.cliente?.nome_completo || orcamento.atendimento?.nome || '';
      const matchesSearch = nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        orcamento.descricao.toLowerCase().includes(searchTerm.toLowerCase()) ||
        orcamento.numero?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'todos' || orcamento.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });

    // Ordenação
    switch (ordenacao) {
      case 'nome_asc':
        result.sort((a, b) => {
          const nomeA = a.cliente?.nome_completo || a.atendimento?.nome || '';
          const nomeB = b.cliente?.nome_completo || b.atendimento?.nome || '';
          return nomeA.localeCompare(nomeB, 'pt-BR');
        });
        break;
      case 'nome_desc':
        result.sort((a, b) => {
          const nomeA = a.cliente?.nome_completo || a.atendimento?.nome || '';
          const nomeB = b.cliente?.nome_completo || b.atendimento?.nome || '';
          return nomeB.localeCompare(nomeA, 'pt-BR');
        });
        break;
      case 'antigo':
        result.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
        break;
      case 'recente':
      default:
        result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
    }

    return result;
  }, [orcamentos, searchTerm, statusFilter, ordenacao]);

  const getStatusColor = (status) => {
    const colors = {
      'Pendente': 'bg-amber-100 text-amber-700',
      'Aprovado': 'bg-green-100 text-green-700',
      'Rejeitado': 'bg-red-100 text-red-700',
      'Expirado': 'bg-slate-100 text-slate-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) {
    return <div className="p-8" data-testid="orcamentos-loading">Carregando...</div>;
  }

  return (
    <div className="p-8 space-y-6" data-testid="orcamentos-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Orçamentos</h1>
          <p className="text-slate-600 mt-1">Crie e gerencie orçamentos para clientes e atendimentos</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-orcamento-button">
              <Plus className="w-4 h-4 mr-2" />
              Novo Orçamento
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingOrcamento ? 'Editar Orçamento' : 'Criar Novo Orçamento'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Vincular a</Label>
                <div className="flex gap-2 mt-1">
                  <Button
                    type="button"
                    variant={vinculoTipo === 'cliente' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setVinculoTipo('cliente')}
                  >
                    Cliente
                  </Button>
                  <Button
                    type="button"
                    variant={vinculoTipo === 'atendimento' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setVinculoTipo('atendimento')}
                  >
                    Atendimento
                  </Button>
                </div>
              </div>
              
              {vinculoTipo === 'cliente' ? (
                <div>
                  <Label htmlFor="cliente_id">Cliente *</Label>
                  <Select
                    value={formData.cliente_id}
                    onValueChange={(value) => setFormData({ ...formData, cliente_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="orcamento-cliente-select">
                      <SelectValue placeholder="Selecione um cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      {clientes.map((cliente) => (
                        <SelectItem key={cliente.id} value={cliente.id}>
                          {cliente.nome_completo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label htmlFor="atendimento_id">Atendimento *</Label>
                  <Select
                    value={formData.atendimento_id}
                    onValueChange={(value) => setFormData({ ...formData, atendimento_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="orcamento-atendimento-select">
                      <SelectValue placeholder="Selecione um atendimento" />
                    </SelectTrigger>
                    <SelectContent>
                      {atendimentos.map((atendimento) => (
                        <SelectItem key={atendimento.id} value={atendimento.id}>
                          {atendimento.nome} - {atendimento.assunto}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label htmlFor="descricao">Descrição do Serviço *</Label>
                <textarea
                  id="descricao"
                  className="w-full min-h-[100px] p-2 border rounded-md"
                  required
                  placeholder="Descreva os serviços incluídos no orçamento..."
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  data-testid="orcamento-descricao-input"
                />
              </div>
              
              <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                <Label className="text-sm font-semibold text-slate-700">Valores dos Honorários</Label>
                
                {/* Valor à Vista */}
                <div>
                  <Label htmlFor="valor_a_vista">Valor à Vista *</Label>
                  <Input
                    id="valor_a_vista"
                    type="number"
                    step="0.01"
                    required
                    placeholder="0.00"
                    value={formData.valor_a_vista}
                    onChange={(e) => setFormData({ ...formData, valor_a_vista: e.target.value })}
                    data-testid="orcamento-valor-avista-input"
                  />
                </div>

                {/* Valor Parcelado */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="parcelas_parcelado">Parcelado em</Label>
                    <Select
                      value={formData.parcelas_parcelado}
                      onValueChange={(value) => setFormData({ ...formData, parcelas_parcelado: value })}
                    >
                      <SelectTrigger data-testid="parcelas-parcelado-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                          <SelectItem key={n} value={n.toString()}>{n}x</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="valor_parcelado">Valor Total Parcelado *</Label>
                    <Input
                      id="valor_parcelado"
                      type="number"
                      step="0.01"
                      required
                      placeholder="0.00"
                      value={formData.valor_parcelado}
                      onChange={(e) => setFormData({ ...formData, valor_parcelado: e.target.value })}
                      data-testid="orcamento-valor-parcelado-input"
                    />
                  </div>
                </div>
                {formData.valor_parcelado && parseInt(formData.parcelas_parcelado) > 1 && (
                  <p className="text-xs text-slate-500">
                    = {formData.parcelas_parcelado}x de {formatCurrency(parseFloat(formData.valor_parcelado) / parseInt(formData.parcelas_parcelado))}
                  </p>
                )}

                {/* Entrada + Parcelamento */}
                <div className="pt-2 border-t">
                  <Label className="text-sm text-slate-600">Entrada + Parcelamento (opcional)</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div>
                      <Label htmlFor="valor_entrada" className="text-xs">Entrada</Label>
                      <Input
                        id="valor_entrada"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={formData.valor_entrada}
                        onChange={(e) => setFormData({ ...formData, valor_entrada: e.target.value })}
                        data-testid="orcamento-valor-entrada-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="parcelas_entrada" className="text-xs">Parcelas</Label>
                      <Select
                        value={formData.parcelas_entrada}
                        onValueChange={(value) => setFormData({ ...formData, parcelas_entrada: value })}
                      >
                        <SelectTrigger data-testid="parcelas-entrada-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                            <SelectItem key={n} value={n.toString()}>{n}x</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="valor_parcela_entrada" className="text-xs">Valor da Parcela</Label>
                      <Input
                        id="valor_parcela_entrada"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={formData.valor_parcela_entrada}
                        onChange={(e) => setFormData({ ...formData, valor_parcela_entrada: e.target.value })}
                        data-testid="orcamento-valor-parcela-entrada-input"
                      />
                    </div>
                  </div>
                  {formData.valor_entrada && formData.valor_parcela_entrada && (
                    <p className="text-xs text-slate-500 mt-1">
                      = {formatCurrency(parseFloat(formData.valor_entrada))} + {formData.parcelas_entrada}x de {formatCurrency(parseFloat(formData.valor_parcela_entrada))}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="validade_dias">Validade (dias)</Label>
                <Input
                  id="validade_dias"
                  type="number"
                  value={formData.validade_dias}
                  onChange={(e) => setFormData({ ...formData, validade_dias: e.target.value })}
                  data-testid="orcamento-validade-input"
                />
              </div>

              <div>
                <Label htmlFor="observacoes">Observações</Label>
                <textarea
                  id="observacoes"
                  className="w-full min-h-[60px] p-2 border rounded-md"
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-orcamento-button">
                  {editingOrcamento ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Dialog Gerar PDF */}
      <Dialog open={isPdfDialogOpen} onOpenChange={setIsPdfDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gerar PDF do Orçamento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Selecione os documentos que o cliente deve providenciar:
            </p>
            
            <div className="space-y-2 max-h-[300px] overflow-y-auto border rounded-md p-3">
              {tiposDocumentos.map((tipo) => (
                <div key={tipo.id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={tipo.id}
                      checked={selectedDocumentos.includes(tipo.nome)}
                      onCheckedChange={() => handleToggleDocumento(tipo.nome)}
                    />
                    <label htmlFor={tipo.id} className="text-sm cursor-pointer">
                      <span className="font-medium">{tipo.nome}</span>
                      {tipo.descricao && (
                        <span className="text-slate-500 ml-2">- {tipo.descricao}</span>
                      )}
                    </label>
                  </div>
                  {!tipo.padrao && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteTipo(tipo.id)}
                      className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                    >
                      ×
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Adicionar novo tipo */}
            <Dialog open={isNovoTipoDialogOpen} onOpenChange={setIsNovoTipoDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="w-full">
                  <FilePlus className="w-4 h-4 mr-2" />
                  Adicionar Novo Tipo de Documento
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Novo Tipo de Documento</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateNovoTipo} className="space-y-4">
                  <div>
                    <Label htmlFor="novo_nome">Nome *</Label>
                    <Input
                      id="novo_nome"
                      required
                      value={novoTipoDoc.nome}
                      onChange={(e) => setNovoTipoDoc({ ...novoTipoDoc, nome: e.target.value })}
                      placeholder="Ex: Contrato de Compra e Venda"
                    />
                  </div>
                  <div>
                    <Label htmlFor="novo_descricao">Descrição</Label>
                    <Input
                      id="novo_descricao"
                      value={novoTipoDoc.descricao}
                      onChange={(e) => setNovoTipoDoc({ ...novoTipoDoc, descricao: e.target.value })}
                      placeholder="Descrição opcional"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsNovoTipoDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                      Criar
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsPdfDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleGerarPdf} 
                className="bg-slate-900 hover:bg-slate-800"
                disabled={generatingPdf}
                data-testid="gerar-pdf-confirm-button"
              >
                {generatingPdf ? (
                  'Gerando...'
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Gerar PDF
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Buscar por cliente, descrição ou número..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-orcamentos-input"
          />
        </div>
        <div className="w-full sm:w-48">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="orcamento-filter-select">
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os status</SelectItem>
              <SelectItem value="Pendente">Pendente</SelectItem>
              <SelectItem value="Aprovado">Aprovado</SelectItem>
              <SelectItem value="Rejeitado">Rejeitado</SelectItem>
              <SelectItem value="Expirado">Expirado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-48">
          <Select value={ordenacao} onValueChange={setOrdenacao}>
            <SelectTrigger data-testid="ordenacao-orcamentos-select">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recente">Mais recente</SelectItem>
              <SelectItem value="antigo">Mais antigo</SelectItem>
              <SelectItem value="nome_asc">Cliente (A-Z)</SelectItem>
              <SelectItem value="nome_desc">Cliente (Z-A)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-4">
        {filteredOrcamentos.length > 0 ? (
          filteredOrcamentos.map((orcamento) => (
            <Card key={orcamento.id} className="hover:shadow-lg transition-shadow" data-testid="orcamento-card">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                      <FileText className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{orcamento.numero}</CardTitle>
                        <span className={`text-xs px-2 py-1 rounded ${getStatusColor(orcamento.status)}`}>
                          {orcamento.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        {orcamento.cliente?.nome_completo || orcamento.atendimento?.nome}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-slate-900">
                      À vista: {formatCurrency(orcamento.valor_a_vista || orcamento.valor_total)}
                    </p>
                    <p className="text-sm text-slate-600">
                      Parcelado: {(orcamento.parcelas_parcelado || 1) > 1 
                        ? `${orcamento.parcelas_parcelado}x de ${formatCurrency((orcamento.valor_parcelado || orcamento.valor_total) / orcamento.parcelas_parcelado)}`
                        : formatCurrency(orcamento.valor_parcelado || orcamento.valor_total)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Válido até: {new Date(orcamento.data_validade).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-700 mb-3">{orcamento.descricao}</p>
                {orcamento.valor_entrada && orcamento.valor_parcela_entrada && (
                  <p className="text-sm text-slate-600 mb-3">
                    <strong>Entrada + Parcelamento:</strong> {formatCurrency(orcamento.valor_entrada)} + {orcamento.parcelas_entrada || 1}x de {formatCurrency(orcamento.valor_parcela_entrada)}
                  </p>
                )}
                <div className="flex gap-2 mt-3 pt-3 border-t flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenPdfDialog(orcamento)}
                    data-testid="gerar-pdf-button"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Gerar PDF
                  </Button>
                  {orcamento.status === 'Pendente' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={() => handleAprovar(orcamento.id)}
                        data-testid="aprovar-orcamento-button"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Aprovar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleRejeitar(orcamento.id)}
                        data-testid="rejeitar-orcamento-button"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Rejeitar
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(orcamento)}
                    data-testid="edit-orcamento-button"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(orcamento.id)}
                    data-testid="delete-orcamento-button"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="text-center py-12 text-slate-500">
            Nenhum orçamento encontrado
          </div>
        )}
      </div>
    </div>
  );
};

export default Orcamentos;
